/**
 * CLIENT-SIDE INSTRUMENTATION (instrumentation-client.ts)
 * 
 * This file handles client-side (browser) instrumentation for the Next.js application.
 * It runs in the browser before React hydration and user interactions begin.
 * 
 * Purpose:
 * - Initialize client-side error monitoring and performance tracking
 * - Set up Sentry with browser-specific features like session replay
 * - Track client-side navigation and user interactions
 * - Handle client-side errors and performance monitoring
 * 
 * Key Features:
 * - Runs exclusively in the browser (client-side only)
 * - Uses NEXT_PUBLIC_SENTRY_DSN (client-accessible environment variable)
 * - Includes session replay for debugging user interactions
 * - Tracks router transitions for navigation monitoring
 * - Configurable via environment variables with fallbacks
 * 
 * Browser-Specific Integrations:
 * - Session Replay: Records user sessions for debugging
 * - Console Integration: Captures console errors/warnings
 * - Router Transition Tracking: Monitors navigation performance
 * 
 * Note: This file must be manually imported where needed, unlike instrumentation.ts
 * which is auto-discovered by Next.js.
 * 
 * Usage: Import this file in your _app.tsx or other entry point:
 * import './instrumentation-client'
 * 
 * Currently this file is not being imported anywhere, so the client-side Sentry
 * initialization may not be running. Consider importing it in _app.tsx if needed.
 */

;globalThis["_sentryRewritesTunnelPath"] = undefined;globalThis["SENTRY_RELEASE"] = {"id":"BN6669"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryNextJsVersion"] = "16.2.6";globalThis["_sentryRewriteFramesAssetPrefixPath"] = "/ct/assets/resources/air-desktop-pwa";globalThis["_sentryAssetPrefix"] = "https://static-assets-ct.flixcart.com/ct/assets/resources/air-desktop-pwa/";globalThis["_sentryExperimentalThirdPartyOriginStackFrames"] = undefined;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[],\"staticRoutes\":[]}";import * as Sentry from "@sentry/nextjs";

import { getSentryEnvironmentConfig } from '@/utils/general/environment';
import { allFilters } from "@/utils/analytics/sentry/sentryFilters";

function getSpanDurationMs(span: any): number {
  const start = typeof span?.start_timestamp === 'number' ? span.start_timestamp : undefined;
  const end = typeof span?.timestamp === 'number' ? span.timestamp : undefined;
  if (start === undefined || end === undefined) return 0;
  return Math.max(0, (end - start) * 1000);
}

// Get environment-specific configuration with fallbacks to env vars
const envConfig = getSentryEnvironmentConfig();

// Only initialize Sentry if DSN is provided
const sentryDsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

if (!sentryDsn) {
  console.warn('Sentry DSN not provided. Sentry will not be initialized.');
} else {
  Sentry.init({
    dsn: sentryDsn,
    environment: envConfig.environment,
    release: envConfig.release,
    
    // Sampling rates with environment variable overrides
    tracesSampleRate: parseFloat(
      process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE || 
      envConfig.tracesSampleRate.toString()
    ),
    replaysOnErrorSampleRate: parseFloat(
      process.env.NEXT_PUBLIC_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || 
      envConfig.replaysOnErrorSampleRate.toString()
    ),
    replaysSessionSampleRate: parseFloat(
      process.env.NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || 
      envConfig.replaysSessionSampleRate.toString()
    ),
    
    // Debug mode configuration
    debug: process.env.NEXT_PUBLIC_SENTRY_DEBUG === 'true' || 
           (process.env.NEXT_PUBLIC_SENTRY_DEBUG === undefined && envConfig.debug),
    
    // Environment-specific breadcrumbs with env var override
    maxBreadcrumbs: parseInt(
      process.env.NEXT_PUBLIC_SENTRY_MAX_BREADCRUMBS || 
      envConfig.maxBreadcrumbs.toString()
    ),
    
    // Browser-specific integrations
    integrations: [
      // Note: Console integration removed due to compatibility issues with Sentry v10.12.0
      // Console errors will still be captured through global error handlers
      
      // Only use integrations that are definitely available in @sentry/nextjs
    ],
    
    // Send PII based on environment (disabled in production for privacy)
    sendDefaultPii: envConfig.sendDefaultPii,
    
    // Ignore common third-party and browser extension errors
    ignoreErrors: [
      // Browser extension conflicts
      'Cannot redefine property',
      'Cannot redefine property: create',
      // Generic script errors (usually from blocked scripts or extensions)
      'Script error.',
      'Script error',
      'NetworkError',
      'Load failed',
      // Browser-specific errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
    
    // URLs to ignore errors from (browser extensions, third-party scripts)
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      /^safari-web-extension:\/\//i,
      // WebAuthn interceptors (password managers)
      /webauthninterceptor/i,
      // Common third-party scripts that cause noise
      /googletagmanager\.com/i,
      /google-analytics\.com/i,
      /facebook\.net/i,
      /connect\.facebook\.net/i,
    ],
    
    // Handle network/certificate issues and local debugging
    beforeSend(event, hint) {
            
      // Log errors locally if Sentry upload fails or debug is enabled
      const shouldLog = process.env.NEXT_PUBLIC_SENTRY_DEBUG === 'true' || 
                       (process.env.NEXT_PUBLIC_SENTRY_DEBUG === undefined && envConfig.debug);
      
      if (shouldLog) {
        console.group('🚨 Sentry Error Event');
        console.error('Event:', event);
        console.error('Hint:', hint);
        console.groupEnd();
      }

      const matchedFilter = allFilters.find(filter => filter.match(event, hint))
      if (matchedFilter) {
        return null
      }
      
      return event;
    },
    
    // Filter transactions and limit spans to reduce usage
    beforeSendTransaction(event) {
      // Drop noisy/low-value client transactions (dominates span volume on busy apps).
      const tx = (event?.transaction || '').toString();
      if (
        tx.includes('/_next/') ||
        tx.includes('favicon.ico') ||
        tx.includes('service-worker') ||
        tx.includes('/sw.js')
      ) {
        return null;
      }

      if (!event.spans) {
        return event;
      }
      
      // Limit the number of spans per transaction (keep first 50 most important)
      const maxSpans = parseInt(
        process.env.NEXT_PUBLIC_SENTRY_MAX_SPANS || 
        envConfig.maxSpans.toString()
      );
      
      // Filter out low-value spans to reduce usage
      const filteredSpans = event.spans
        .filter((span: any) => {
          const spanName = span.description || span.op || '';
          const spanOp = span.op || '';
      
          // ✅ KEEP: Your own API calls
          if (spanOp.includes('http')) {
            return spanName.includes('cleartrip') || spanName.includes('flixcart');
          }
      
          // ✅ KEEP: React component renders (useful for spotting slow renders)
          if (spanOp.includes('ui.react')) {
            return true;
          }
      
          // ✅ KEEP: Long tasks (browser flagging >50ms main thread blocks)
          if (spanOp === 'ui.long-task' || spanOp === 'ui.long-animation-frame') {
            return true;
          }
      
          // ✅ KEEP: Web Vitals markers (LCP, FCP, CLS, INP, TTFB)
          if (spanOp.startsWith('mark.') || spanOp.startsWith('paint.')) {
            return true;
          }
      
          // ❌ DROP: Everything else — browser dns/connect, resource loads,
          //    third-party scripts, navigation internals, generic measures
          return false;
        })
        // Keep the most expensive spans, not just the earliest ones.
        .sort((a: any, b: any) => getSpanDurationMs(b) - getSpanDurationMs(a))
        .slice(0, maxSpans); // Limit total spans
      
      event.spans = filteredSpans;
      return event;
    },
  });
}

// Export router transition hook for navigation instrumentation
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
